import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { listJobs, type ReindexJob, type ReindexStatus } from '@/services/reindex-api';
import { Plus } from 'lucide-react';

function statusVariant(status: ReindexStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed':
      return 'default';
    case 'failed':
    case 'cancelled':
      return 'destructive';
    case 'paused':
    case 'awaiting_confirmation':
      return 'secondary';
    default:
      return 'outline';
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function progressLabel(job: ReindexJob): string {
  if (job.totalEstimate == null) {
    return `${job.enqueuedCount} enqueued / ${job.ackedCount} indexed`;
  }
  return `${job.ackedCount} / ${job.totalEstimate} indexed`;
}

export function ReindexJobList() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reindex-jobs'],
    queryFn: () => listJobs({ limit: 100 }),
    refetchInterval: 10000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Bulk Reindex</h2>
          <p className="text-muted-foreground">
            Backfill or refresh entities into an OpenSearch index. Pulls IDs
            from Affinity v2 and posts them to the existing webhook pipeline.
          </p>
        </div>
        <Link to="/reindex/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New Reindex
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-sm text-destructive">
              Failed to load jobs: {(error as Error).message}
            </div>
          ) : !data || data.jobs.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No reindex jobs yet. Click <strong>New Reindex</strong> to kick one off.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Target Index</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Started By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.jobs.map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => {
                      window.location.assign(`/reindex/${job.id}`);
                    }}
                  >
                    <TableCell>
                      <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
                    </TableCell>
                    <TableCell>{job.entityType}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{job.mode}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{job.targetIndex}</TableCell>
                    <TableCell className="text-sm">{progressLabel(job)}</TableCell>
                    <TableCell className="text-sm">{formatDate(job.startedAt)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {job.createdBy}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
